import React, { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'expo-router';
import { StyleSheet } from 'react-native';

import { LoadingAnimation, useIsFocused, useResponsive, webOnlyStyles } from '@sg/garnish';
import { ReorderRail } from '@sg/shared-fe-features-menu';

import { useContentfulContentTypeEntry } from '@order/Contentful';
import {
  HomeV2,
  useHomeContent,
  type HomeContent,
  getBannerContent,
  useHomeMenu,
  useHomeFiltering,
  HomeProductCategory,
  useHomeLastCanceledOrder,
} from '@order/features-home';
import { useTelemetry } from '@order/Telemetry';
import { useUsableNetBannerHeight } from '@order/usable-net';

import { AppFooter, MenuReorderRail } from '../../../../components';
import {
  getLastInteractedDeliveryDetails,
  useDietaryPreferencesMachine,
  useLastInteractedStore,
} from '../../../../context';
import { useIsLoggedIn } from '../../../../context/AuthMachine';
import {
  useLastOrderStatus,
  useMenuReorderRailOrders,
  useOneClickAddToBag,
} from '../../../../hooks';
import { isPendingOrder, isPendingOrderStatus, isPendingFeedbackOrder } from '../../utils';

export const HomeScreenContentV2 = () => {
  const isLoggedIn = useIsLoggedIn();
  const isFocused = useIsFocused();
  const router = useRouter();
  const { track } = useTelemetry();
  const { currentBreakpoint: { isXS }, match } = useResponsive(); // prettier-ignore

  // ─── Styles ────────────────────────────────────────────────────────────────
  // So that the banner takes up the full height of the screen on larger viewports.

  const usableNetBannerHeight = useUsableNetBannerHeight();
  const bannerHeight = match([null, webOnlyStyles({ minHeight: `calc(100svh - ${usableNetBannerHeight}px)` as never })]); // prettier-ignore

  // ─── Menu ──────────────────────────────────────────────────────────────────

  const lastInteractedStore = useLastInteractedStore() ?? {};
  const { restaurantId, restaurantSlug = '', restaurantName, addressId } = lastInteractedStore;
  const deliveryDetails = getLastInteractedDeliveryDetails(lastInteractedStore);

  const { products, isLoadingMenu } = useHomeMenu({ restaurantId, addressId });

  // ─── Allergens ─────────────────────────────────────────────────────────────

  const { restrictions: dietaryPreferences } = useDietaryPreferencesMachine();

  // ─── Content ───────────────────────────────────────────────────────────────

  const entry = useContentfulContentTypeEntry<HomeContent>({ contentType: 'homeScreen', include: 2 }); // prettier-ignore
  const content = useHomeContent(entry?.data?.fields);
  const isLoadingContent = entry?.fetching;

  // ─── Filtering ─────────────────────────────────────────────────────────────

  const { filterableCategories, genericCategories } = useHomeFiltering({
    products,
    filterableCategories: content.filterableCategories,
    genericCategories: content.genericCategories,
  });

  // ─── Chips State ───────────────────────────────────────────────────────────

  const [selectedCategory, setSelectedCategory] = useState<HomeProductCategory | null>(null);
  const handleCategorySelection = useCallback(
    (category: HomeProductCategory | null) => {
      track('home.filter.selected', { filterName: category?.categoryName ?? null });
      setSelectedCategory(category);
    },
    [track],
  );

  // ─── Reorder ───────────────────────────────────────────────────────────────

  const { orders, mostRecentLineItems, mostRecentOrder, isFetchingMostRecentOrders } =
    useMenuReorderRailOrders({
      restaurantId: Number.isNaN(Number(restaurantId)) ? 0 : Number(restaurantId),
      enabled: isLoggedIn && Boolean(restaurantId),
    });

  // Filtering should only hide the reorder rail on XS.
  const shouldHideReorderRailOnXS = Boolean(selectedCategory) && isXS;

  const canRenderReorderRail =
    !shouldHideReorderRailOnXS &&
    restaurantId &&
    restaurantSlug &&
    restaurantName &&
    mostRecentLineItems.length > 0;

  // ─── Pending Order ───────────────────────────────────────────────────────
  // Loads the last order and its order status from cache (requested on home screen).

  const pendingOrder = useLastOrderStatus({ requestPolicy: 'cache-only' });
  const isLoadingPendingOrder = pendingOrder?.isLoadingLastOrder;
  const hasPendingOrder = isPendingOrderStatus(pendingOrder?.orderStatus);

  // ─── Multiple Pending Orders ───────────────────────────────────────────────
  // Extra requirement to show when there are multiple pending orders.

  const pendingOrders = useMemo(() => orders.filter(isPendingOrder), [orders]);
  const hasMultiplePendingOrders = pendingOrders.length > 1;

  // ─── Last Canceled Order ───────────────────────────────────────────────────

  const { lastCanceledOrderId, hasRecentCanceledOrder } = useHomeLastCanceledOrder({ pause: !isLoggedIn || !isFocused }); // prettier-ignore

  // ─── Pending Feedback ──────────────────────────────────────────────────────

  const pendingFeedbackOrder = orders.find(isPendingFeedbackOrder);

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const { addProductToBag } = useOneClickAddToBag({
    restaurantSlug,
    deliveryDetails,
  });

  const openMenu = useCallback(() => {
    router.navigate('/menu');
  }, [router]);

  const trackMenuView = useCallback(() => {
    track('home.view_menu.selected');
  }, [track]);

  const openProductDetails = useCallback(
    (slug: string) => {
      if (deliveryDetails?.addressId) {
        router.navigate({
          pathname: '/delivery/[addressId]/[productSlug]',
          params: { addressId: deliveryDetails.addressId, productSlug: slug },
        });
        return;
      }

      router.navigate({
        pathname: '/[restaurantSlug]/[productSlug]',
        params: { restaurantSlug, productSlug: slug },
      });
    },
    [deliveryDetails?.addressId, restaurantSlug, router],
  );

  const handleAddProductToBag = useCallback(
    (id: string, name: string) => {
      return addProductToBag({ id, name }, 1);
    },
    [addProductToBag],
  );

  const viewOrders = useCallback(() => {
    track('home.post_order.view_order.selected');

    if (hasMultiplePendingOrders) {
      router.navigate('/account/orders');
      return;
    }

    if (hasPendingOrder) {
      router.navigate(`/order-status/${pendingOrder.orderId}`);
    }
  }, [router, hasMultiplePendingOrders, hasPendingOrder, pendingOrder, track]);

  const viewCanceledOrder = useCallback(() => {
    if (!lastCanceledOrderId) return;

    router.navigate(`/order-status/${lastCanceledOrderId}`);
  }, [router, lastCanceledOrderId]);

  const openRewards = useCallback(() => {
    track('home.post_order.points_cta.selected');
    router.navigate('/rewards');
  }, [router, track]);

  const rateOrder = useCallback(() => {
    if (!pendingFeedbackOrder) return;

    track('home.post_order.rate_order.selected');
    router.navigate(`/feedback/${pendingFeedbackOrder.id}`);
  }, [router, pendingFeedbackOrder, track]);

  const postOrderBanner = match([content.postOrderBannerXs, content.postOrderBannerSm]);

  const trackPostOrderBannerCtaPress = useCallback(() => {
    track('home.post_order.campaign.selected', { campaignId: postOrderBanner.id });
  }, [track, postOrderBanner.id]);

  const trackItemSelection = useCallback(
    (product: string, category: string) => {
      track('home.product.selected', { product, category });
    },
    [track],
  );

  // ───────────────────────────────────────────────────────────────────────────

  if (isLoadingContent || isLoadingMenu || isFetchingMostRecentOrders || isLoadingPendingOrder) {
    return <LoadingAnimation />;
  }

  if (hasPendingOrder || hasMultiplePendingOrders) {
    const order = pendingOrder ?? pendingOrders[0];
    const product = order?.lineItems[0]?.product;
    const cardContent = hasMultiplePendingOrders ? content.postOrderCardMultiple : content.postOrderCard; // prettier-ignore
    const hasEarnedLoyaltyPoints = pendingOrder.hasEarnedLoyaltyPoints || pendingOrders.some((order) => order.hasEarnedLoyaltyPoints); // prettier-ignore

    return (
      <HomeV2.Container>
        <HomeV2.PostOrderContent>
          {isXS ? (
            <>
              <HomeV2.OrderCard
                title={cardContent.title}
                description={cardContent.description}
                cta={cardContent.cta}
                image={product?.asset?.url}
                imageAlt={product?.name}
                orderCount={order.lineItems.length}
                onPress={viewOrders}
              />

              <HomeV2.OrderBanner
                title={postOrderBanner.title}
                description={postOrderBanner.description}
                image={postOrderBanner.image}
                imageAlt={postOrderBanner.imageA11y}
                deepLink={postOrderBanner.deepLink}
                foregroundColor={postOrderBanner.foregroundColor}
                cta={postOrderBanner.cta}
                onPress={trackPostOrderBannerCtaPress}
              />

              {hasEarnedLoyaltyPoints ? (
                <HomeV2.LoyaltyCard
                  title={content.postOrderLoyalty.title}
                  description={content.postOrderLoyalty.description}
                  cta={content.postOrderLoyalty.cta}
                  onPress={openRewards}
                />
              ) : null}
            </>
          ) : (
            <>
              <HomeV2.OrderBanner.Image
                image={postOrderBanner.image}
                imageAlt={postOrderBanner.imageA11y}
              />

              <HomeV2.PostOrderContent.Footer>
                <HomeV2.OrderBanner
                  title={postOrderBanner.title}
                  description={postOrderBanner.description}
                  deepLink={postOrderBanner.deepLink}
                  foregroundColor={postOrderBanner.foregroundColor}
                  cta={postOrderBanner.cta}
                  onPress={trackPostOrderBannerCtaPress}
                />

                <HomeV2.PostOrderContent.Info>
                  <HomeV2.OrderCard
                    title={cardContent.title}
                    description={cardContent.description}
                    cta={cardContent.cta}
                    image={product?.asset?.url}
                    imageAlt={product?.name}
                    orderCount={order.lineItems.length}
                    onPress={viewOrders}
                  />

                  {hasEarnedLoyaltyPoints ? (
                    <HomeV2.LoyaltyCard
                      title={content.postOrderLoyalty.title}
                      description={content.postOrderLoyalty.description}
                      cta={content.postOrderLoyalty.cta}
                      onPress={openRewards}
                    />
                  ) : null}
                </HomeV2.PostOrderContent.Info>
              </HomeV2.PostOrderContent.Footer>
            </>
          )}
        </HomeV2.PostOrderContent>

        {isXS ? null : <AppFooter />}
      </HomeV2.Container>
    );
  }

  return (
    <>
      <HomeV2.Container stickyHeaderIndices={hasRecentCanceledOrder ? [0] : []}>
        {hasRecentCanceledOrder && isXS ? (
          <HomeV2.OrderCanceledBanner onPress={viewCanceledOrder} />
        ) : null}

        <HomeV2.Container.Content>
          <HomeV2.Banner
            style={bannerHeight}
            size={isLoggedIn ? 'small' : 'large'}
            content={getBannerContent(content, isLoggedIn, isXS)}
          />

          {canRenderReorderRail && !isXS ? (
            <HomeV2.ReorderRail.Container>
              <ReorderRail.Header style={styles.reorderRail}>
                <HomeV2.ReorderRail.SideWrapper />

                <ReorderRail.Heading />

                <HomeV2.ReorderRail.SideWrapper>
                  <MenuReorderRail.SeeAll
                    restaurantId={restaurantId}
                    deliveryDetails={deliveryDetails}
                  />
                </HomeV2.ReorderRail.SideWrapper>
              </ReorderRail.Header>

              <MenuReorderRail.List
                location="home"
                style={styles.reorderRail}
                lineItems={mostRecentLineItems}
                mostRecentOrder={mostRecentOrder}
                restaurantId={restaurantId}
                restaurantName={restaurantName}
                restaurantSlug={restaurantSlug}
                deliveryDetails={deliveryDetails}
              />
            </HomeV2.ReorderRail.Container>
          ) : null}

          {pendingFeedbackOrder && !isXS ? (
            <HomeV2.RateOrderCard
              image={pendingFeedbackOrder.lineItems[0]?.product?.asset?.url}
              orderCount={pendingFeedbackOrder.lineItems.length}
              onPress={rateOrder}
            />
          ) : null}

          {filterableCategories.length > 0 ? (
            <HomeV2.FilterableCategories
              title={content.filtersTitle}
              filterableCategories={filterableCategories}
              dietaryPreferences={dietaryPreferences}
              selectedCategory={selectedCategory}
              onSelectCategory={handleCategorySelection}
              openMenu={openMenu}
              openProductDetails={openProductDetails}
              addProductToBag={handleAddProductToBag}
              trackItemSelection={trackItemSelection}
            />
          ) : null}

          {canRenderReorderRail && isXS ? (
            <HomeV2.ReorderRail.Container>
              <ReorderRail.Header style={styles.reorderRail}>
                <ReorderRail.Heading />

                <MenuReorderRail.SeeAll
                  restaurantId={restaurantId}
                  deliveryDetails={deliveryDetails}
                />
              </ReorderRail.Header>

              <MenuReorderRail.List
                style={styles.reorderRail}
                lineItems={mostRecentLineItems}
                mostRecentOrder={mostRecentOrder}
                restaurantId={restaurantId}
                restaurantName={restaurantName}
                restaurantSlug={restaurantSlug}
                deliveryDetails={deliveryDetails}
              />
            </HomeV2.ReorderRail.Container>
          ) : null}

          {pendingFeedbackOrder && !selectedCategory && isXS ? (
            <HomeV2.RateOrderCard
              image={pendingFeedbackOrder.lineItems[0]?.product?.asset?.url}
              orderCount={pendingFeedbackOrder.lineItems.length}
              onPress={rateOrder}
            />
          ) : null}

          {genericCategories.length > 0 && !selectedCategory ? (
            <HomeV2.Container.AnimatedContent>
              <HomeV2.GenericCategories
                genericCategories={genericCategories}
                dietaryPreferences={dietaryPreferences}
                openMenu={openMenu}
                openProductDetails={openProductDetails}
                addProductToBag={handleAddProductToBag}
                trackItemSelection={trackItemSelection}
              />
            </HomeV2.Container.AnimatedContent>
          ) : null}

          <HomeV2.Footer
            menuHref="/menu"
            title={content.footerTitle}
            imageUrl={content.footerImageUrl}
            imageA11y={content.footerImageA11y}
            trackMenuView={trackMenuView}
          />
        </HomeV2.Container.Content>

        {isXS ? null : <AppFooter />}
      </HomeV2.Container>

      {hasRecentCanceledOrder && !isXS ? (
        <HomeV2.OrderCanceledBanner onPress={viewCanceledOrder} />
      ) : null}
    </>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  reorderRail: {
    paddingHorizontal: 0, // override default padding
  },
});
